import React, { useEffect, useRef, useState } from "react";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import CircularProgress from "@material-ui/core/CircularProgress";
import Divider from "@material-ui/core/Divider";
import { makeStyles } from "@material-ui/core/styles";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import ButtonWithSpinner from "../ButtonWithSpinner";
import { formatTaskDate, mergeCommentsChronologically } from "../../pages/Tasks/taskUtils";

const MAX_COMMENT_LENGTH = 5000;

const useStyles = makeStyles(theme => ({
  comment: {
    padding: theme.spacing(1, 0)
  },
  body: {
    whiteSpace: "pre-wrap",
    overflowWrap: "break-word",
    wordBreak: "break-word"
  },
  centered: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(2)
  },
  form: {
    marginTop: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1)
  }
}));

// Valida localmente antes do envio — mesma regra do backend, mas o
// backend continua sendo quem decide de verdade.
const validateCommentBody = value => {
  if (typeof value !== "string") return "invalid";
  const trimmed = value.trim();
  if (trimmed.length === 0) return "required";
  if (trimmed.length > MAX_COMMENT_LENGTH) return "tooLong";
  return null;
};

// Aba de comentários — própria página/hasMore/loading/sequência de
// requisição. Disponível em qualquer status, inclusive done/canceled.
//
// `readOnly` (opcional, default false — retrocompatível) oculta o
// formulário de novo comentário e o botão de enviar; a listagem continua
// disponível normalmente.
const TaskCommentsTab = ({ taskId, readOnly = false }) => {
  const classes = useStyles();
  const [comments, setComments] = useState([]);
  const [count, setCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [body, setBody] = useState("");
  const [bodyError, setBodyError] = useState(null);
  const [sending, setSending] = useState(false);

  const isMountedRef = useRef(true);
  const requestIdRef = useRef(0);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchPage = (targetPage, isLoadMore) => {
    const currentRequestId = ++requestIdRef.current;
    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    (async () => {
      try {
        const { data } = await api.get(`/tasks/${taskId}/comments`, {
          params: { pageNumber: targetPage }
        });

        if (!isMountedRef.current || currentRequestId !== requestIdRef.current) return;

        setComments(prev => mergeCommentsChronologically(isLoadMore ? prev : [], data.comments));
        setHasMore(data.hasMore);
        setCount(data.count);
      } catch (err) {
        if (!isMountedRef.current || currentRequestId !== requestIdRef.current) return;
        toastError(err);
      } finally {
        if (isMountedRef.current && currentRequestId === requestIdRef.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    })();
  };

  useEffect(() => {
    requestIdRef.current += 1;
    setComments([]);
    setHasMore(false);
    setPageNumber(1);
    setCount(0);
    fetchPage(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId]);

  const handleLoadMore = () => {
    if (loading || loadingMore || !hasMore) return;
    const nextPage = pageNumber + 1;
    setPageNumber(nextPage);
    fetchPage(nextPage, true);
  };

  const handleSend = async () => {
    if (sending) return;

    const error = validateCommentBody(body);
    if (error) {
      setBodyError(error);
      return;
    }

    setSending(true);
    try {
      const { data } = await api.post(`/tasks/${taskId}/comments`, { body: body.trim() });

      if (!isMountedRef.current) return;

      // Insere localmente a partir da resposta, deduplica e reordena —
      // não recarrega a página inteira de comentários.
      setComments(prev => mergeCommentsChronologically(prev, [data]));
      setCount(prev => prev + 1);
      setBody("");
      setBodyError(null);
    } catch (err) {
      if (!isMountedRef.current) return;
      toastError(err);
    } finally {
      if (isMountedRef.current) setSending(false);
    }
  };

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        {i18n.t("tasks.comments.title")} ({count})
      </Typography>

      {loading ? (
        <Box className={classes.centered}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <>
          {comments.length === 0 ? (
            <Box className={classes.centered}>
              <Typography color="textSecondary">{i18n.t("tasks.comments.empty")}</Typography>
            </Box>
          ) : (
            comments.map((comment, index) => (
              <React.Fragment key={comment.id}>
                <Box className={classes.comment}>
                  <Typography variant="caption" color="textSecondary">
                    {comment.userName || i18n.t("tasks.table.none")} —{" "}
                    {formatTaskDate(comment.createdAt, "DD/MM/YYYY HH:mm") ||
                      i18n.t("tasks.details.noDate")}
                  </Typography>
                  <Typography variant="body2" className={classes.body}>
                    {comment.body}
                  </Typography>
                </Box>
                {index < comments.length - 1 && <Divider />}
              </React.Fragment>
            ))
          )}

          {hasMore && (
            <Box className={classes.centered}>
              <Button onClick={handleLoadMore} disabled={loadingMore} size="small">
                {loadingMore ? <CircularProgress size={18} /> : i18n.t("tasks.comments.loadMore")}
              </Button>
            </Box>
          )}
        </>
      )}

      {!readOnly && (
        <>
          <Divider />

          <Box className={classes.form}>
            <TextField
              label={i18n.t("tasks.comments.newComment")}
              variant="outlined"
              size="small"
              fullWidth
              multiline
              minRows={2}
              value={body}
              onChange={e => {
                setBody(e.target.value);
                setBodyError(null);
              }}
              error={Boolean(bodyError)}
              helperText={bodyError ? i18n.t(`tasks.comments.errors.${bodyError}`) : ""}
            />
            <Box display="flex" justifyContent="flex-end">
              <ButtonWithSpinner
                onClick={handleSend}
                color="primary"
                variant="contained"
                loading={sending}
                disabled={sending}
              >
                {i18n.t("tasks.comments.send")}
              </ButtonWithSpinner>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

export default TaskCommentsTab;
